// src/pages/ceed/QuotesOversightPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FileText, RefreshCw, CheckCircle, Clock, XCircle, Send,
  ThumbsUp, ThumbsDown, Upload, Download, AlertCircle, User,
  Calendar, Phone, Mail, MapPin, MessageSquare, ChevronDown,
  ChevronUp, Building,
} from "lucide-react";

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth <= 767);
  useEffect(() => {
    const h = () => setV(window.innerWidth <= 767);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return v;
}

/* ── Style injection ──────────────────────────────────────────── */
const STYLE_ID = "ceed-quotes-styles";
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .qo-wrap { font-family: 'DM Sans', sans-serif; color: #0c1118; }

    /* Page header */
    .qo-page-title {
      font-family: 'DM Sans', sans-serif;
      font-size: 24px; font-weight: 700; color: #0c1118; line-height: 1.2;
    }
    .qo-page-sub {
      font-family: 'DM Sans', sans-serif;
      font-size: 13px; color: #6b7280; margin-top: 4px;
    }

    /* KPI row */
    .qo-kpi-row {
      display: grid; grid-template-columns: repeat(4,1fr); gap: 12px;
      margin: 20px 0 24px;
    }
    @media (max-width: 1023px) { .qo-kpi-row { grid-template-columns: repeat(2,1fr); } }
    @media (max-width: 479px)  { .qo-kpi-row { grid-template-columns: 1fr; } }
    .qo-kpi {
      background: #fff; border: 1px solid #e8ecf0; border-radius: 10px;
      padding: 14px 18px; position: relative; overflow: hidden;
    }
    .qo-kpi-accent {
      position: absolute; top: 0; left: 0; bottom: 0; width: 4px;
      border-radius: 10px 0 0 10px;
    }
    .qo-kpi-val {
      font-family: 'DM Sans', sans-serif;
      font-size: 2.2rem; font-weight: 700; line-height: 1; color: #0c1118; letter-spacing: .02em;
    }
    .qo-kpi-lbl {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; letter-spacing: .04em;
      color: #9ca3af; margin-top: 3px;
    }

    /* Filter bar */
    .qo-filter-bar {
      display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 20px;
    }
    .qo-filter-btn {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 14px; border-radius: 7px; font-size: 13px; font-weight: 500;
      cursor: pointer; border: 1px solid #e2e8f0; background: #fff;
      color: #6b7280; transition: all 150ms; white-space: nowrap;
    }
    .qo-filter-btn:hover { border-color: #d1d5db; color: #374151; background: #f9fafb; }
    .qo-filter-btn.active { background: #0c1118; color: #fff; border-color: #0c1118; }
    .qo-filter-count {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; padding: 1px 6px; border-radius: 4px;
    }
    .qo-filter-btn.active .qo-filter-count { background: rgba(255,255,255,.2); color: #fff; }
    .qo-filter-btn:not(.active) .qo-filter-count { background: #f3f4f6; color: #9ca3af; }

    /* Table card */
    .qo-table-card {
      background: #fff; border: 1px solid #e8ecf0; border-radius: 10px; overflow: hidden;
    }
    .qo-table-wrap { overflow-x: auto; }
    .qo-table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .qo-table thead tr { background: #f9fafb; border-bottom: 1px solid #e8ecf0; }
    .qo-table th {
      padding: 10px 16px; text-align: left;
      font-family: 'DM Sans', sans-serif;
      font-size: 9.5px; letter-spacing: .04em;
      color: #9ca3af; white-space: nowrap;
    }
    .qo-table td { padding: 13px 16px; vertical-align: middle; }
    .qo-table tbody tr { border-bottom: 1px solid #f3f4f6; transition: background 120ms; }
    .qo-table tbody tr:last-child { border-bottom: none; }
    .qo-table tbody tr.expanded { background: #fafbff; }
    .qo-table tbody tr:hover:not(.detail-row) { background: #fafafa; }

    /* Expand toggle */
    .qo-expand-btn {
      width: 26px; height: 26px; border-radius: 6px;
      background: #f4f5f7; border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      color: #6b7280; transition: background 150ms, color 150ms;
    }
    .qo-expand-btn:hover { background: #e8ecf0; color: #0c1118; }

    /* Status badges */
    .qo-badge {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600;
    }
    .qo-badge-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
    .qo-badge-draft           { background: #f1f5f9; color: #475569; }
    .qo-badge-draft .qo-badge-dot           { background: #94a3b8; }
    .qo-badge-pending_review  { background: rgba(0,0,0,0.06); color: #333333; }
    .qo-badge-pending_review .qo-badge-dot  { background: #111111; }
    .qo-badge-approved        { background: rgba(16,185,129,.1); color: #065f46; }
    .qo-badge-approved .qo-badge-dot        { background: #10b981; }
    .qo-badge-rejected        { background: rgba(239,68,68,.1); color: #991b1b; }
    .qo-badge-rejected .qo-badge-dot        { background: #ef4444; }
    .qo-badge-sent_to_client  { background: rgba(99,102,241,.1); color: #3730a3; }
    .qo-badge-sent_to_client .qo-badge-dot  { background: #6366f1; }
    .qo-badge-accepted        { background: rgba(5,150,105,.1); color: #064e3b; }
    .qo-badge-accepted .qo-badge-dot        { background: #059669; }
    .qo-badge-declined        { background: rgba(244,63,94,.1); color: #9f1239; }
    .qo-badge-declined .qo-badge-dot        { background: #f43f5e; }

    /* Action buttons */
    .qo-act-btn {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 5px 10px; border-radius: 6px; font-size: 11.5px; font-weight: 500;
      cursor: pointer; border: none; transition: background 150ms; white-space: nowrap;
    }
    .qo-act-upload  { background: #f4f5f7; color: #374151; }
    .qo-act-upload:hover { background: #e8ecf0; }
    .qo-act-send    { background: #0c1118; color: #fff; }
    .qo-act-send:hover { background: #1e2832; }
    .qo-act-resend  { background: #f4f5f7; color: #374151; }
    .qo-act-resend:hover { background: #e8ecf0; }
    .qo-act-approve { background: rgba(16,185,129,.1); color: #065f46; }
    .qo-act-approve:hover { background: rgba(16,185,129,.18); }
    .qo-act-hold    { background: rgba(0,0,0,0.06); color: #333333; }
    .qo-act-hold:hover { background: rgba(0,0,0,0.10); }
    .qo-act-download { color: #4f46e5; font-size: 12px; text-decoration: none; display: inline-flex; align-items: center; gap: 5px; font-weight: 500; }
    .qo-act-download:hover { color: #3730a3; }

    /* Avatar circle */
    .qo-avatar {
      width: 30px; height: 30px; border-radius: 50%;
      background: #f4f5f7; border: 1px solid #e8ecf0;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; color: #9ca3af;
    }

    /* Detail row expansion */
    .qo-detail {
      background: #f9fafb; border-top: 1px solid #e8ecf0;
      padding: 20px 20px 20px 52px;
    }
    .qo-detail-grid {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;
    }
    @media (max-width: 767px) { .qo-detail-grid { grid-template-columns: 1fr; } }
    .qo-detail-section-title {
      font-family: 'DM Sans', sans-serif;
      font-size: 9.5px; letter-spacing: .04em;
      color: #9ca3af; margin-bottom: 10px;
    }
    .qo-detail-row {
      display: flex; align-items: flex-start; gap: 8px;
      font-size: 13px; color: #374151; margin-bottom: 6px;
    }
    .qo-detail-row svg { flex-shrink: 0; margin-top: 2px; color: #9ca3af; }

    /* Empty state */
    .qo-empty {
      padding: 64px 20px; text-align: center;
    }
    .qo-empty-title { font-size: 14px; font-weight: 600; color: #374151; margin-top: 12px; }
    .qo-empty-sub { font-size: 12px; color: #9ca3af; margin-top: 4px; }

    /* Loading */
    .qo-loading {
      display: flex; align-items: center; justify-content: center; gap: 10px;
      padding: 64px 20px; font-size: 13px; color: #9ca3af;
    }
    .qo-spinner {
      width: 20px; height: 20px; border: 2px solid #e8ecf0;
      border-top-color: #0c1118; border-radius: 50%;
      animation: qoSpin .7s linear infinite; flex-shrink: 0;
    }
    @keyframes qoSpin { to { transform: rotate(360deg); } }

    /* Upload modal */
    .qo-modal-backdrop {
      position: fixed; inset: 0; z-index: 50;
      background: rgba(0,0,0,.6); backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center; padding: 20px;
    }
    .qo-modal {
      background: #fff; border-radius: 12px; overflow: hidden;
      width: 100%; max-width: 440px;
      box-shadow: 0 20px 60px rgba(0,0,0,.2);
    }
    .qo-modal-head {
      padding: 20px 24px 16px; border-bottom: 1px solid #f3f4f6;
    }
    .qo-modal-title { font-size: 16px; font-weight: 600; color: #0c1118; }
    .qo-modal-sub { font-size: 13px; color: #9ca3af; margin-top: 2px; }
    .qo-modal-body { padding: 20px 24px; }
    .qo-modal-foot {
      padding: 14px 24px; border-top: 1px solid #f3f4f6;
      display: flex; justify-content: flex-end; gap: 10px;
    }
    .qo-drop-zone {
      border: 2px dashed #e2e8f0; border-radius: 10px;
      padding: 32px 20px; text-align: center; cursor: pointer;
      transition: border-color 160ms, background 160ms;
    }
    .qo-drop-zone:hover, .qo-drop-zone.drag-over {
      border-color: #111111; background: rgba(0,0,0,0.02);
    }
    .qo-drop-filename {
      font-size: 13px; font-weight: 500; color: #0c1118; margin-top: 8px;
    }
    .qo-drop-hint { font-size: 12px; color: #9ca3af; margin-top: 4px; }

    /* Btn variants */
    .qo-btn {
      display: inline-flex; align-items: center; gap: 7px;
      padding: 8px 16px; border-radius: 7px; font-size: 13px; font-weight: 500;
      cursor: pointer; border: none; transition: background 150ms;
    }
    .qo-btn-dark { background: #0c1118; color: #fff; }
    .qo-btn-dark:hover { background: #1e2832; }
    .qo-btn-dark:disabled { opacity: .5; cursor: default; }
    .qo-btn-outline { background: #fff; color: #374151; border: 1px solid #e2e8f0; }
    .qo-btn-outline:hover { background: #f9fafb; }

    /* Toast */
    .qo-toast {
      position: fixed; bottom: 24px; right: 24px; z-index: 60;
      display: flex; align-items: center; gap: 10px;
      padding: 12px 18px; border-radius: 10px;
      font-size: 13.5px; font-weight: 500; color: #fff;
      box-shadow: 0 8px 24px rgba(0,0,0,.2);
      animation: qoToastIn .25s ease;
    }
    .qo-toast-success { background: #0c1118; }
    .qo-toast-error   { background: #dc2626; }
    @keyframes qoToastIn {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @media (max-width: 767px) {
      .qo-kpi-row { grid-template-columns: repeat(2,1fr) !important; }
      .qo-kpi { padding: 12px 14px; }
      .qo-kpi-val { font-size: 1.6rem; }
      .qo-toast { right: 16px; left: 16px; bottom: 16px; }
    }
  `;
  document.head.appendChild(el);
}

/* ── Helpers ──────────────────────────────────────────────────── */
const BASE = process.env.REACT_APP_BACKEND_URL || "";

async function jfetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...opts,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`${res.status} ${res.statusText}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  return res.json();
}

const STATUS_META = {
  draft:          { label: "Draft",           cls: "qo-badge-draft" },
  pending_review: { label: "Pending Review",  cls: "qo-badge-pending_review" },
  approved:       { label: "Approved",        cls: "qo-badge-approved" },
  rejected:       { label: "Rejected",        cls: "qo-badge-rejected" },
  sent_to_client: { label: "Sent to Client",  cls: "qo-badge-sent_to_client" },
  accepted:       { label: "Accepted",        cls: "qo-badge-accepted" },
  declined:       { label: "Declined",        cls: "qo-badge-declined" },
};

function StatusBadge({ s }) {
  const meta = STATUS_META[s] || { label: String(s || "").replaceAll("_", " "), cls: "qo-badge-draft" };
  return (
    <span className={`qo-badge ${meta.cls}`}>
      <span className="qo-badge-dot" />
      {meta.label}
    </span>
  );
}

const FILTERS = [
  { value: "all",            label: "All" },
  { value: "pending_review", label: "Pending Review" },
  { value: "approved",       label: "Approved" },
  { value: "sent_to_client", label: "Sent to Client" },
];

const KPI_ACCENTS = ["#0c1118", "#111111", "#10b981", "#6366f1"];

/* ── Upload Modal ─────────────────────────────────────────────── */
function UploadModal({ quote, onClose, onSuccess, showToast }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${BASE}/api/quotes/${quote.id}/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error("Upload failed");
      showToast("Quote PDF uploaded successfully");
      onSuccess();
      onClose();
    } catch {
      showToast("Upload failed. Please try again.", "error");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="qo-modal-backdrop" onClick={onClose}>
      <div className="qo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qo-modal-head">
          <div className="qo-modal-title">Upload Quote PDF</div>
          <div className="qo-modal-sub">
            For: <strong>{quote.project?.name || quote.projectId}</strong>
          </div>
        </div>
        <div className="qo-modal-body">
          <div
            className={`qo-drop-zone ${dragOver ? "drag-over" : ""}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); setFile(e.dataTransfer.files?.[0] || null); }}
          >
            <Upload size={28} style={{ color: "#d1d5db", margin: "0 auto" }} />
            {file ? (
              <div className="qo-drop-filename">{file.name}</div>
            ) : (
              <>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#374151", marginTop: "8px" }}>
                  Click to select or drag & drop
                </div>
                <div className="qo-drop-hint">PDF, DOC, DOCX, CSV, XLS, XLSX</div>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.csv,.xls,.xlsx"
              style={{ display: "none" }}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
        </div>
        <div className="qo-modal-foot">
          <button className="qo-btn qo-btn-outline" onClick={onClose}>Cancel</button>
          <button
            className="qo-btn qo-btn-dark"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? (
              <><div className="qo-spinner" style={{ width: 14, height: 14 }} /> Uploading…</>
            ) : (
              <><Upload size={14} /> Upload</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Detail expansion row ─────────────────────────────────────── */
function QuoteDetailRow({ q }) {
  const ci = q.contactInfo || {};
  return (
    <tr className="detail-row">
      <td colSpan={7} style={{ padding: 0 }}>
        <div className="qo-detail">
          <div className="qo-detail-grid">
            {/* Contact */}
            <div>
              <div className="qo-detail-section-title">Contact Details</div>
              <div className="qo-detail-row">
                <User size={13} />
                {[ci.firstName, ci.lastName].filter(Boolean).join(" ") || "—"}
              </div>
              <div className="qo-detail-row">
                <Mail size={13} />
                {ci.email || "—"}
              </div>
              <div className="qo-detail-row">
                <Phone size={13} />
                {ci.phone || "—"}
              </div>
              <div className="qo-detail-row">
                <MapPin size={13} />
                {[ci.address, ci.city, ci.state, ci.zip].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
            {/* Building */}
            <div>
              <div className="qo-detail-section-title">About the Building</div>
              <div className="qo-detail-row">
                <MessageSquare size={13} />
                {ci.buildingDescription || <em style={{ color: "#9ca3af" }}>No description provided</em>}
              </div>
            </div>
            {/* Project */}
            <div>
              <div className="qo-detail-section-title">Project</div>
              <div className="qo-detail-row">
                <Building size={13} />
                <strong>{q.project?.name || q.projectId}</strong>
              </div>
              {q.project?.street && (
                <div className="qo-detail-row">
                  <MapPin size={13} />
                  {[q.project.street, q.project.city, q.project.state, q.project.zip].filter(Boolean).join(", ")}
                </div>
              )}
              <div className="qo-detail-row">
                <Calendar size={13} />
                Submitted {new Date(q.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
              </div>
              {q.fileUrl && (
                <a href={q.fileUrl} target="_blank" rel="noreferrer" className="qo-act-download" style={{ marginTop: "6px", display: "inline-flex" }}>
                  <Download size={13} /> Download Current Quote
                </a>
              )}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

/* ── Toast ────────────────────────────────────────────────────── */
function ToastBar({ toast }) {
  if (!toast) return null;
  return (
    <div className={`qo-toast ${toast.type === "success" ? "qo-toast-success" : "qo-toast-error"}`}>
      {toast.type === "success" ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
      {toast.message}
    </div>
  );
}

function QuoteMobileCard({ q, onUpload, onSendToClient, onApprove, onHold }) {
  const ci = q.contactInfo || {};
  const fullName = [ci.firstName, ci.lastName].filter(Boolean).join(" ") || ci.email || "Unknown";
  return (
    <div style={{ background: "#fff", border: "1px solid #e8ecf0", borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#0c1118", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.project?.name || q.projectId}</div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{fullName}</div>
          {ci.email && <div style={{ fontSize: 11, color: "#9ca3af" }}>{ci.email}</div>}
        </div>
        <StatusBadge s={q.status} />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9ca3af" }}>{new Date(q.createdAt).toLocaleDateString()}</span>
        {q.fileUrl && (
          <a href={q.fileUrl} target="_blank" rel="noreferrer" className="qo-act-download">
            <Download size={12} /> Download PDF
          </a>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        <button className="qo-act-btn qo-act-upload" onClick={() => onUpload(q)}>
          <Upload size={12} />{q.fileUrl ? "Replace PDF" : "Upload PDF"}
        </button>
        {q.fileUrl && q.status !== "sent_to_client" && q.status !== "accepted" && (
          <button className="qo-act-btn qo-act-send" onClick={() => onSendToClient(q.id)}>
            <Send size={12} /> Send
          </button>
        )}
        {q.fileUrl && (q.status === "sent_to_client" || q.status === "accepted") && (
          <button className="qo-act-btn qo-act-resend" onClick={() => onSendToClient(q.id)}>
            <Send size={12} /> Resend
          </button>
        )}
        {q.status !== "approved" && q.status !== "sent_to_client" && !q.fileUrl && (
          <button className="qo-act-btn qo-act-approve" onClick={() => onApprove(q.id)}>
            <CheckCircle size={12} /> Approve
          </button>
        )}
        {(q.status === "approved" || q.status === "sent_to_client") && (
          <button className="qo-act-btn qo-act-hold" onClick={() => onHold(q.id)}>
            <Clock size={12} /> Hold
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────── */
export default function QuotesOversightPage() {
  useEffect(() => { injectStyles(); }, []);
  const isMobile = useIsMobile();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  async function load() {
    setLoading(true);
    try {
      const r = await jfetch("/api/quotes/admin");
      setItems(r.items || []);
    } catch {
      showToast("Failed to load quote pool", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function approve(id) {
    try {
      await jfetch(`/api/quotes/${id}/approve`, { method: "POST" });
      showToast("Quote approved");
      await load();
    } catch {
      showToast("Failed to approve quote", "error");
    }
  }

  async function hold(id) {
    try {
      await jfetch(`/api/quotes/${id}/hold`, { method: "POST" });
      showToast("Quote set to pending review");
      await load();
    } catch {
      showToast("Failed to update quote", "error");
    }
  }

  async function sendToClient(id) {
    try {
      await jfetch(`/api/quotes/${id}/mark-sent`, { method: "POST" });
      showToast("Quote sent to client — they will receive an email with the download link.");
      await load();
    } catch (err) {
      let bodyMsg = "";
      try { bodyMsg = JSON.parse(err.body || "{}").message; } catch {}
      if (bodyMsg === "no_file_uploaded_yet") {
        showToast("Upload a PDF file first before sending to client.", "error");
      } else {
        showToast("Failed to send quote to client.", "error");
      }
    }
  }

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((q) => q.status === filter);
  }, [items, filter]);

  const stats = useMemo(() => ({
    total:   items.length,
    pending: items.filter((q) => q.status === "pending_review").length,
    approved: items.filter((q) => q.status === "approved").length,
    sent:    items.filter((q) => q.status === "sent_to_client").length,
  }), [items]);

  const kpis = [
    { label: "Total", value: stats.total },
    { label: "Pending Review", value: stats.pending },
    { label: "Approved", value: stats.approved },
    { label: "Sent to Client", value: stats.sent },
  ];

  return (
    <div className="qo-wrap">
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "4px" }}>
        <div>
          <div className="qo-page-title">Quote Pool</div>
          <div className="qo-page-sub">Individual user quote requests · CEED Console</div>
        </div>
        <button className="qo-btn qo-btn-dark" onClick={load} disabled={loading} title="Refresh">
          <RefreshCw size={14} style={{ animation: loading ? "qoSpin .7s linear infinite" : "none" }} />
          {!isMobile && "Refresh"}
        </button>
      </div>

      {/* ── KPI row ── */}
      <div className="qo-kpi-row" style={isMobile ? { gridTemplateColumns: "repeat(2,1fr)" } : undefined}>
        {kpis.map((k, i) => (
          <div className="qo-kpi" key={k.label}>
            <div className="qo-kpi-accent" style={{ background: KPI_ACCENTS[i] }} />
            <div className="qo-kpi-val">{k.value}</div>
            <div className="qo-kpi-lbl">{k.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filter bar ── */}
      <div className="qo-filter-bar" style={isMobile ? { flexWrap: "nowrap", overflowX: "auto", paddingBottom: 4 } : undefined}>
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            className={`qo-filter-btn ${filter === value ? "active" : ""}`}
            onClick={() => setFilter(value)}
          >
            {label}
            {value !== "all" && (
              <span className="qo-filter-count">
                {items.filter((q) => q.status === value).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table / Mobile cards */}
      {isMobile ? (
        <div>
          {loading && items.length === 0 ? (
            <div className="qo-loading"><div className="qo-spinner" />Loading quote pool…</div>
          ) : filtered.length === 0 ? (
            <div className="qo-empty">
              <FileText size={40} style={{ color: "#e2e8f0", margin: "0 auto" }} />
              <div className="qo-empty-title">No quotes found</div>
              <div className="qo-empty-sub">{filter === "all" ? "No individual user quote requests yet." : `No quotes with status "${filter.replaceAll("_", " ")}".`}</div>
            </div>
          ) : (
            filtered.map((q) => (
              <QuoteMobileCard
                key={q.id} q={q}
                onUpload={setUploadTarget}
                onSendToClient={sendToClient}
                onApprove={approve}
                onHold={hold}
              />
            ))
          )}
        </div>
      ) : (
        <div className="qo-table-card">
          {loading && items.length === 0 ? (
            <div className="qo-loading">
              <div className="qo-spinner" />
              Loading quote pool…
            </div>
          ) : (
            <div className="qo-table-wrap">
              <table className="qo-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }} />
                    <th>Project</th>
                    <th>Requester</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Quote File</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => {
                    const isExpanded = expandedId === q.id;
                    const ci = q.contactInfo || {};
                    const fullName = [ci.firstName, ci.lastName].filter(Boolean).join(" ") || ci.email || "Unknown";

                    return (
                      <React.Fragment key={q.id}>
                        <tr className={isExpanded ? "expanded" : ""}>
                          <td style={{ width: 40 }}>
                            <button className="qo-expand-btn" onClick={() => setExpandedId(isExpanded ? null : q.id)}>
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, color: "#0c1118" }}>{q.project?.name || q.projectId}</div>
                            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", color: "#d1d5db", marginTop: "2px" }}>{q.id.slice(0, 12)}…</div>
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
                              <div className="qo-avatar"><User size={13} /></div>
                              <div>
                                <div style={{ fontWeight: 500, fontSize: "13px", color: "#0c1118" }}>{fullName}</div>
                                {ci.email && <div style={{ fontSize: "11px", color: "#9ca3af" }}>{ci.email}</div>}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ fontSize: "12px", color: "#374151", fontFamily: "'DM Sans', sans-serif" }}>{new Date(q.createdAt).toLocaleDateString()}</div>
                            <div style={{ fontSize: "11px", color: "#9ca3af" }}>{new Date(q.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                          </td>
                          <td><StatusBadge s={q.status} /></td>
                          <td>
                            {q.fileUrl ? (
                              <a href={q.fileUrl} target="_blank" rel="noreferrer" className="qo-act-download">
                                <Download size={13} /> Download PDF
                              </a>
                            ) : (
                              <span style={{ fontSize: "12px", color: "#d1d5db", fontStyle: "italic" }}>Not uploaded</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px", flexWrap: "wrap" }}>
                              <button className="qo-act-btn qo-act-upload" onClick={() => setUploadTarget(q)}>
                                <Upload size={12} />{q.fileUrl ? "Replace PDF" : "Upload PDF"}
                              </button>
                              {q.fileUrl && q.status !== "sent_to_client" && q.status !== "accepted" && (
                                <button className="qo-act-btn qo-act-send" onClick={() => sendToClient(q.id)}>
                                  <Send size={12} /> Send to Client
                                </button>
                              )}
                              {q.fileUrl && (q.status === "sent_to_client" || q.status === "accepted") && (
                                <button className="qo-act-btn qo-act-resend" onClick={() => sendToClient(q.id)} title="Resend email notification">
                                  <Send size={12} /> Resend
                                </button>
                              )}
                              {q.status !== "approved" && q.status !== "sent_to_client" && !q.fileUrl && (
                                <button className="qo-act-btn qo-act-approve" onClick={() => approve(q.id)}>
                                  <CheckCircle size={12} /> Approve
                                </button>
                              )}
                              {(q.status === "approved" || q.status === "sent_to_client") && (
                                <button className="qo-act-btn qo-act-hold" onClick={() => hold(q.id)}>
                                  <Clock size={12} /> Hold
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && <QuoteDetailRow q={q} />}
                      </React.Fragment>
                    );
                  })}

                  {!filtered.length && !loading && (
                    <tr>
                      <td colSpan={7}>
                        <div className="qo-empty">
                          <FileText size={40} style={{ color: "#e2e8f0", margin: "0 auto" }} />
                          <div className="qo-empty-title">No quotes found</div>
                          <div className="qo-empty-sub">
                            {filter === "all" ? "No individual user quote requests yet." : `No quotes with status "${filter.replaceAll("_", " ")}".`}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Upload modal ── */}
      {uploadTarget && (
        <UploadModal
          quote={uploadTarget}
          onClose={() => setUploadTarget(null)}
          onSuccess={load}
          showToast={showToast}
        />
      )}

      {/* ── Toast ── */}
      <ToastBar toast={toast} />
    </div>
  );
}
